import { useState, useEffect } from "react";
import { fetchAPI } from "@/lib/api";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter
} from "@/components/ui/dialog";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Video, Image as ImageIcon, Pencil } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import { FileUpload } from "@/components/admin/FileUpload";

interface MediaItem {
  id: number;
  title: string;
  src: string;
  category: string;
  type: "image" | "video";
  video_url?: string;
  orientation?: "portrait" | "landscape";
}

const initialForm = {
  title: "",
  src: "",
  category: "",
  type: "image",
  video_url: "",
  orientation: "landscape"
};

export default function GalleryManager() {
  const [items, setItems] = useState<MediaItem[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState(initialForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchGallery();
    fetchCategories();
  }, []);

  const fetchGallery = () => {
    setLoading(true);
    fetchAPI("?type=gallery")
      .then((data: any) => {
        setItems(data);
        setLoading(false);
      });
  };

  const fetchCategories = () => {
    fetchAPI("?type=categories")
      .then((data: any) => setCategories(data));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const url = editingId ? `?type=gallery&id=${editingId}` : "?type=gallery";
      const method = editingId ? "PUT" : "POST";
      
      const result = await fetchAPI(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (result.status === "success") {
        toast({ title: "Success", description: `Item ${editingId ? "updated" : "added"} successfully` });
        setIsDialogOpen(false);
        setFormData(initialForm);
        setEditingId(null);
        fetchGallery();
      } else {
        throw new Error(result.message);
      }
    } catch (err: any) {
      toast({ variant: "destructive", title: "Error", description: err.message });
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this item?")) return;
    try {
      await fetchAPI(`?type=gallery&id=${id}`, { method: "DELETE" });
      fetchGallery();
    } catch (err) {
      console.error(err);
    }
  };

  const handleEdit = (item: MediaItem) => {
    setFormData({
      title: item.title,
      src: item.src,
      category: item.category,
      type: item.type,
      video_url: item.video_url || "",
      orientation: item.orientation || "landscape"
    });
    setEditingId(item.id);
    setIsDialogOpen(true);
  };

  return (
    <div className="space-y-6">
       <div className="flex items-center justify-between">
        <h1 className="text-3xl font-serif font-bold">Gallery</h1>
        <Button onClick={() => setIsDialogOpen(true)} className="gap-2">
          <Plus className="w-4 h-4" /> Add Media
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {loading ? (
             <div className="col-span-full text-center">Loading...</div>
        ) : items.map((item) => (
             <div key={item.id} className={cn(
                 "group relative bg-muted rounded-xl overflow-hidden shadow-sm",
                 item.orientation === "portrait" ? "aspect-[3/4] row-span-2" : "aspect-video"
             )}>
                <img 
                    src={item.src ? (item.src.startsWith('http') ? item.src : `https://baelleaves.com/${item.src}`) : (item.video_url && item.video_url.includes('youtube') 
                        ? `https://img.youtube.com/vi/${item.video_url.split('v=')[1]?.split('&')[0]}/hqdefault.jpg` 
                        : "")} 
                    alt={item.title} 
                    className="w-full h-full object-cover" 
                    loading="lazy"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-4">
                    <p className="text-white font-medium truncate">{item.title}</p>
                    <p className="text-white/80 text-xs">{item.category}</p>
                    {item.type === "video" && <Video className="absolute top-2 right-2 text-white w-5 h-5" />}
                    
                    <Button 
                        variant="destructive" 
                        size="icon" 
                        className="absolute top-2 left-2 w-8 h-8 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleDelete(item.id)}
                    >
                        <Trash2 className="w-4 h-4" />
                    </Button>

                    <Button 
                        variant="secondary" 
                        size="icon" 
                        className="absolute top-2 left-12 w-8 h-8 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleEdit(item)}
                    >
                        <Pencil className="w-4 h-4" />
                    </Button>
                </div>
             </div>
        ))}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={(open) => {
        setIsDialogOpen(open);
        if (!open) {
          setFormData(initialForm);
          setEditingId(null);
        }
      }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
                <DialogTitle>{editingId ? "Edit Gallery Item" : "Add Gallery Item"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                    <Label>Title</Label>
                    <Input value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} required />
                </div>
                
                <div className="space-y-2">
                    <Label>Source File</Label>
                    <FileUpload 
                        value={formData.src} 
                        onChange={(url) => setFormData({...formData, src: url})}
                        accept={formData.type === "video" ? "video/*,image/*" : "image/*"}
                         placeholder="Upload file"
                    />
                </div>

                <div className="space-y-2">
                    <Label>Type</Label>
                    <Select value={formData.type} onValueChange={(val: any) => setFormData({...formData, type: val})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="image">Image</SelectItem>
                            <SelectItem value="video">Video</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {formData.type === "video" && (
                     <div className="space-y-2">
                        <Label>Video URL</Label>
                        <div className="flex flex-col gap-2">
                            <Input 
                                placeholder="YouTube URL or file URL" 
                                value={formData.video_url || ""} 
                                onChange={(e) => setFormData({...formData, video_url: e.target.value})}
                            />
                            <p className="text-xs text-muted-foreground">Paste a YouTube URL here or leave blank to upload a file below.</p>
                            
                            <FileUpload 
                                value={formData.video_url || ""} 
                                onChange={(url) => setFormData({...formData, video_url: url})}
                                accept="video/*"
                                placeholder="Or upload video file"
                            />
                        </div>
                     </div>
                )}

                <div className="space-y-2">
                    <Label>Orientation</Label>
                    <Select value={formData.orientation} onValueChange={(val: any) => setFormData({...formData, orientation: val})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="landscape">Landscape</SelectItem>
                            <SelectItem value="portrait">Portrait</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <div className="space-y-2">
                    <Label>Category</Label>
                     <Select 
                        value={formData.category} 
                        onValueChange={(val) => setFormData({...formData, category: val})}
                    >
                        <SelectTrigger><SelectValue placeholder="Select Category" /></SelectTrigger>
                        <SelectContent>
                            {categories.map(c => (
                                <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <DialogFooter>
                    <Button type="submit">{editingId ? "Update Item" : "Add Item"}</Button>
                </DialogFooter>
            </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
